import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * SignaturePad — luxe-themed signature canvas.
 *
 * Captures mouse + touch + pen input through Pointer Events, draws smooth
 * cubic curves, and exposes:
 *   ref.toDataURL()  → base64 PNG with `data:image/png;base64,` prefix
 *   ref.isEmpty()    → true if user hasn't drawn anything yet
 *   ref.clear()      → wipe the canvas
 *
 * No dependency on react-signature-canvas (per user preference: custom).
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 600, height = 220, penColor = "#EAEAEA", testid = "signature-pad" },
  ref,
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const dirtyRef = useRef(false);
  const [, force] = useState(0); // re-render to refresh "Clear" disabled state

  // Initialise once for crispness on HiDPI
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = width * dpr;
    c.height = height * dpr;
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = penColor;
    // Background — solid dark so the exported PNG is readable on white paper too
    ctx.fillStyle = "#111111";
    ctx.fillRect(0, 0, width, height);
  }, [width, height, penColor]);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getXY(e);
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const p = getXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    // Use a quadratic curve halfway for smoother strokes
    const midX = (lastPtRef.current.x + p.x) / 2;
    const midY = (lastPtRef.current.y + p.y) / 2;
    ctx.quadraticCurveTo(lastPtRef.current.x, lastPtRef.current.y, midX, midY);
    ctx.stroke();
    lastPtRef.current = p;
    dirtyRef.current = true;
  };
  const onPointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try {
      canvasRef.current.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Stylus already released or browser doesn't support pointer capture.
      // Safe to ignore — drawing already stopped on the line above.
      console.debug("releasePointerCapture noop:", err);
    }
    force((n) => n + 1);
  };

  useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => !dirtyRef.current,
      clear: () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#111111";
        ctx.fillRect(0, 0, width, height);
        dirtyRef.current = false;
        force((n) => n + 1);
      },
      toDataURL: () => canvasRef.current.toDataURL("image/png"),
    }),
    [width, height],
  );

  return (
    <div className="space-y-2" data-testid={testid}>
      <div
        className="border border-[#D4AF37]/40 bg-[#111] rounded-sm overflow-hidden touch-none"
        style={{ width: "100%", maxWidth: width }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{
            display: "block",
            cursor: "crosshair",
            touchAction: "none",
            width: "100%",
            height: "auto",
            maxWidth: width,
          }}
          data-testid={`${testid}-canvas`}
        />
      </div>
      <div className="flex justify-between items-center text-[0.65rem] mono text-[#7a7a7a]">
        <span>{dirtyRef.current ? "Signature captured" : "Draw your signature above"}</span>
        <button
          type="button"
          onClick={() => {
            const c = canvasRef.current;
            const ctx = c.getContext("2d");
            ctx.fillStyle = "#111111";
            ctx.fillRect(0, 0, width, height);
            dirtyRef.current = false;
            force((n) => n + 1);
          }}
          disabled={!dirtyRef.current}
          className="inline-flex items-center gap-1.5 text-[#C99356] hover:text-[#E5B481] disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-wider"
          data-testid={`${testid}-clear`}
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
